// Status chip helpers for the replies inbox.

export interface ChipInfo {
  label: string;
  tone: "ok" | "working" | "danger" | "info" | "muted";
}

export const draftChip = (message: any): ChipInfo | null => {
  const draft = message.draft;
  if (message.draftPending) return { label: "Writing reply", tone: "info" };
  if (!draft) return message.draftError ? { label: "Draft failed", tone: "danger" } : null;
  switch (draft.status) {
    case "sent":
      return draft.autoSent ? { label: "Auto-sent", tone: "ok" } : { label: "Sent", tone: "ok" };
    case "sending":
      return { label: "Sending", tone: "info" };
    case "failed":
      return { label: "Send failed", tone: "danger" };
    case "rejected":
      return { label: "Rejected", tone: "muted" };
    default:
      break;
  }
  if (draft.decision === "escalate") return { label: "Needs you", tone: "working" };
  if (draft.decision === "no_reply" || draft.status === "skipped") {
    return { label: "No reply needed", tone: "muted" };
  }
  if (draft.status === "approved") return { label: "Approved", tone: "info" };
  return { label: "Needs review", tone: "working" };
};

export const isVideoBusy = (draft: any) =>
  Boolean(draft && draft.video && ["queued", "generating"].includes(draft.video.status));

export const videoChip = (draft: any): ChipInfo | null => {
  const video = draft && draft.video;
  if (!video) return null;
  switch (video.status) {
    case "queued":
    case "generating":
      return { label: "Video rendering", tone: "working" };
    case "ready":
      return video.stale ? { label: "Video outdated", tone: "working" } : { label: "Video ready", tone: "ok" };
    case "failed":
      return { label: "Video failed", tone: "danger" };
    default:
      return null;
  }
};

// Drafts the inbox should keep refreshing: video rendering or send in flight.
export const draftIdsToPoll = (messages: any[]) =>
  messages
    .map((message) => message.draft)
    .filter((draft) => draft && (isVideoBusy(draft) || draft.status === "sending"))
    .map((draft) => draft.id as string);

// Max time the inbox keeps polling for background drafts.
export const DRAFT_POLL_MAX_MS = 3 * 60 * 1000;

export const hasPendingDrafts = (messages: any[]) =>
  messages.some((message) => message && message.draftPending);

export const formatDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};
